import React, { useCallback, useState, useContext, useEffect } from "react";
import i18next from "i18next";
import _ from "lodash";
import { Context } from "states/Store";
import { ERROR } from "services/common/Constants";
import MenuAppBar from "components/MenuAppBar";
import useStyles from "style/js-style/containers/administration/roles/RolesStyle";
import EABackground from "components/EABackground";
import EAIcon from "components/common/EAIcon";
import EAButton from "components/common/EAButton";
import { getUserRoles } from "services/edge/UserService";
import { getAccountRoles, isRoleAssigned } from "services/edge/RoleService";
import RoleCreateDialog from "./dialogs/RoleCreateDialog";
import RoleEditDialog from "./dialogs/RoleEditDialog";
import RoleDeleteDialog from "./dialogs/RoleDeleteDialog";
import PrivilegesPanel from "./privileges/PrivilegesPanel";
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext,
  cleanRoleHierarchy,
  findRoleByIdInHierarchyFromList,
  getAllChildRoles,
  getSortedFlatRoles
} from "services/common/RolesUtils";
import RoleMenu from "./menu/RoleMenu";

import useAccountAttributes from "components/common/hooks/useAccountAttributes";
import { useAuth } from "@ea-webapp/auth";
import {
  formatActivityTextFromActivity,
  getCountries,
} from 'services/common/Utils';

function Roles() {
  const { classes } = useStyles();

  const [state, dispatch] = useContext(Context);
  const { isSigUser, isGlobalAdmin, hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const { authState } = useAuth();

  const [roles, setRoles] = useState([]);
  const [lastRoleID, setLastRoleID] = useState(null);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [selectedRole, setSelectedRole] = useState(null);
  const [isSelectedRoleReadOnly, setIsSelectedRoleReadOnly] = useState(false);
  const [isSelectedRoleAssigned, setIsSelectedRoleAssigned] = useState(false);

  const [accountActivities, setAccountActivities] = useState(null)
  const [accountCountries, setAccountCountries] = useState(null)
  const [isLoaded, setIsLoaded] = useState(false);
  const [qualifyingCriteria, setQualifyingCriteria] = useState([]);
  const [qualifyingCriteriaSelectedrole, setQualifyingCriteriaSelectedrole] = useState([]);

   // Fetch attributes and indicators
   const {
    accountAttributesAndIndicators
  } = useAccountAttributes()

  useEffect(() => {
    if (
      accountActivities !== null &&
      accountCountries !== null &&
      accountAttributesAndIndicators.dossierAttributes !== null &&
      accountAttributesAndIndicators.thirdpartyAttributes !== null
    ) {
      setIsLoaded(true)
    }
  }, [accountActivities, accountCountries, accountAttributesAndIndicators.dossierAttributes, accountAttributesAndIndicators.thirdpartyAttributes])

  useEffect(() => {
    selectedRole && setQualifyingCriteriaSelectedrole(_.find(qualifyingCriteria, { id: selectedRole.id }))
  }, [selectedRole, qualifyingCriteria]);

  useEffect(() => {
    roles && setQualifyingCriteria(getFlatQualifyingCriteria(roles));
  }, [roles]);

  const getFlatQualifyingCriteria = (roles) => {
    const getQualifyingCriteria = (
      role,
      thirdPartyQualifyingCriteria,
      dossierQualifyingCriteria,
      contextQualifyingCriteria,
      authorizedDocumentTypes
    ) => {
      if (role) {
        const mixedThirdPartyQualifyingCriteria = (thirdPartyQualifyingCriteria || []).concat(role.thirdPartyQualifyingCriteria || []);
        const mixedDossierQualifyingCriteria = (dossierQualifyingCriteria || []).concat(role.dossierQualifyingCriteria || []);
        const mixedContextQualifyingCriteria = (contextQualifyingCriteria || []).concat(role.contextQualifyingCriteria || []);
        const mixedAuthorizedDocumentTypes = (authorizedDocumentTypes || []).concat(role.authorizedDocumentTypes || []);

        return {
          'id': role.id,
          'thirdPartyQualifyingCriteria': thirdPartyQualifyingCriteria || [],
          'dossierQualifyingCriteria': dossierQualifyingCriteria || [],
          'contextQualifyingCriteria': contextQualifyingCriteria || [],
          'authorizedDocumentTypes': authorizedDocumentTypes || [],
          'derivedRoles': role.derivedRoles?.map(derivedRole => {
              return getQualifyingCriteria(
                derivedRole,
                mixedThirdPartyQualifyingCriteria,
                mixedDossierQualifyingCriteria,
                mixedContextQualifyingCriteria,
                mixedAuthorizedDocumentTypes,
              )
            }
          ),
        }
      }
    };

    const flattenRoles = (qualifyingCriteria) => {
      let result = [];
      const { derivedRoles, ...values } = qualifyingCriteria;
      result.push(values);

      if (derivedRoles?.length) {
        for (const derivedRole of derivedRoles) {
          result = result.concat(flattenRoles(derivedRole));
        }
      }

      return result;
    }

    if (roles[0]) return flattenRoles(getQualifyingCriteria(roles[0]));
  }

  // Fetch countries & activities
  useEffect(() => {
    async function fetchData() {
      try {
        setAccountActivities(
          state.activityTraductions
            .filter((a) => a.origine === 'DNB')
            .map((activity) => {
              return {
                value: activity.code,
                label: formatActivityTextFromActivity(activity),
                level: activity.level,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label))
        );

        // LIST COUNTRIES
        let distinctCountries = [];
        let allCountries = getCountries(i18next.language);
        for (const [key, value] of Object.entries(allCountries)) {
          distinctCountries.push({ "value": key, "label": value });
        }
        const sortedMap = Array.from(distinctCountries).sort((a, b) => a.label.localeCompare(b.label));
        setAccountCountries(sortedMap);

      } catch (e) {
        dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
      }
    }

    if (state.activityTraductions.length){
      fetchData();
    }
  }, [dispatch, state.account, state.activityTraductions]);

 

  useEffect(() => {
    if (!_.isEmpty(roles)) {
      let lastElement = roles[roles.length - 1];
      while (lastElement.derivedRoles.length > 0) {
        lastElement = lastElement.derivedRoles[lastElement.derivedRoles.length - 1];
      }

      setLastRoleID(lastElement.id)
    }
  }, [roles])

  const getBaseRoleCandidates = useCallback((role) => {
    if (role !== null) {
      //A descendant role can't become a baseRole of an ascendant role
      const childRoleIds = getAllChildRoles(role).map((roleItem) => roleItem.id);

      //Exlude the role itself and all its descendant roles
      let results = getSortedFlatRoles(roles).filter((roleItem) => {
        return !_.includes(childRoleIds, roleItem.id) && roleItem.id !== role.id;
      }).map((r) => {
        return {
          value: r.id,
          label: r.name
        }
      });

      return results;
    } else {
      let results = getSortedFlatRoles(roles).map((r) => {
        return {
          value: r.id,
          label: r.name
        }
      });

      return results;
    }
  }, [roles]);

  const resolveSelectedRole = useCallback((previousRole, availableRoles) => {
    if (_.isEmpty(availableRoles)) {
      return null;
    }
    if (previousRole && previousRole?.id) {
      let foundRole = findRoleByIdInHierarchyFromList(previousRole.id, availableRoles);
      if (foundRole) {
        return foundRole;
      } else if (previousRole.baseRoleId) {
        foundRole = findRoleByIdInHierarchyFromList(previousRole.baseRoleId, availableRoles);
        if (foundRole) {
          return foundRole;
        }
      }
    }
    return _.first(availableRoles);
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      let results = null;

      if (isGlobalAdmin || isSigUser) {
        results = await getAccountRoles(state.account.id, true, true, true, dispatch);
      } else {
        results = await getUserRoles(state.account.id, authState.user.id, true, true, true, dispatch)
      }
      if (!_.isEmpty(results?.content)) {
        setRoles(results.content);
        setSelectedRole((prev) => resolveSelectedRole(prev, results.content));
      }
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: "INTERNAL_ERROR" } });
      setRoles([]);
      setSelectedRole(null);
    }
  }, [state.account, authState.user, dispatch, isGlobalAdmin, isSigUser, resolveSelectedRole]);

  useEffect(() => {
    if (state.account && authState.user && !_.isEmpty(state.roles)) {
      let fetchedRoles = [];

      if (isGlobalAdmin || isSigUser) {//If it's the global admin we start by the default role since all account roles are fetched
        const defaultRole = _.find(state.roles, { default: true });

        fetchedRoles = defaultRole ? [defaultRole] : [];
      } else {
        fetchedRoles = cleanRoleHierarchy(state.roles);
      }

      setRoles(fetchedRoles);
      setSelectedRole((prev) => resolveSelectedRole(prev, fetchedRoles));
    } else {
      setRoles([]);
      setSelectedRole(null);
    }
    // eslint-disable-next-line
  }, [state.account, authState.user, state.roles, isGlobalAdmin, isSigUser, resolveSelectedRole])

  const isOwnedRole = useCallback((role) => {
    let isOwnedRole = false;

    if (!_.isEmpty(state.roles) && role) {
      isOwnedRole = _.some(state.roles, (item) => item.id === role.id);
    }

    return isOwnedRole;
  }, [state.roles]);

  useEffect(() => {
    setIsSelectedRoleReadOnly(isOwnedRole(selectedRole));
  }, [state.roles, selectedRole, isOwnedRole]);

  const checkRoleAssigned = useCallback(async (role) => {
    try {
      const resp = await isRoleAssigned(state.account.id, role.id, dispatch);

      setIsSelectedRoleAssigned(!!resp.content)
    } catch (error) {
      const err =
        error?.message?.messages?.error && error?.message?.messages?.error[0];
      dispatch({
        type: "ALERT",
        alert: { type: ERROR, msg: err?.code || "INTERNAL_ERROR" },
      });
      setIsSelectedRoleAssigned(true);
    }
  }, [state.account, dispatch, setIsSelectedRoleAssigned])

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);
  

  return (
    <div className={classes.root}>
      <MenuAppBar variant={"users"} />
      <EABackground />
      <div className={classes.mainWrapper}>
        {/* ------------- */}
        {/* --- ROLES --- */}
        {/* ------------- */}
        <div className={classes.rolesPanel}>
          {/* Header */}
          <div className={classes.rolesPanelHeader}>
            <EAIcon icon="roles/roles-title" className={classes.headerLogo} />
            <h2 className={classes.rolesPanelHeaderTitle}>
              {i18next.t("roles.title")}
            </h2>
          </div>

          {/* Content */}
          <div className={classes.rolesPanelContent}>
            {roles && <RoleMenu
              roles={roles}
              selectedRole={selectedRole}
              lastRoleID={lastRoleID}
              onRoleEdit={(role) => {
                setSelectedRole(role);
                setIsEditDialogOpen(true);
              }}
              onRoleDelete={(role) => {
                checkRoleAssigned(role);
                setSelectedRole(role);
                setIsDeleteDialogOpen(true);
              }}
              onRoleSelected={(role) => {
                setSelectedRole(role);
              }}
            />
            }
          </div>

          {/* Add roles */}
          <div className={classes.rolesPanelButton}>
            <EAButton
              content={
                <>
                  <span className={classes.crossBtn}>+</span>
                  {i18next.t("roles.addRole")}
                </>
              }
              disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_ROLE)}
              customStyle={{
                width: "280px",
                height: "54px",
                fontSize: "18px",
                borderRadius: "12px",
              }}
              onClick={() => setIsCreateDialogOpen(true)}
            />
          </div>
        </div>

        {/* ------------- */}
        {/* --- RULES --- */}
        {/* ------------- */}
        {isLoaded &&
          <PrivilegesPanel
            role={selectedRole}
            isSelectedRoleReadOnly={isSelectedRoleReadOnly}
            onValidateSuccess={() => {
              fetchRoles();
            }}
            accountActivities={accountActivities}
            accountCountries={accountCountries}
            accountAttributesAndIndicators={accountAttributesAndIndicators}
            qualifyingCriteriaSelectedrole={qualifyingCriteriaSelectedrole}
          />
        }

        {/* PopUp : on create */}
        {isCreateDialogOpen && (
          <RoleCreateDialog
            baseRoleCandidates={getBaseRoleCandidates(null)}
            isOpen={isCreateDialogOpen}
            onClose={() => setIsCreateDialogOpen(false)}
            onValidateSuccess={() => {
              setIsCreateDialogOpen(false);
              fetchRoles();
            }}
          />
        )}

        {/* PopUp : on edit */}
        {isEditDialogOpen && (
          <RoleEditDialog
            role={selectedRole}
            baseRoleCandidates={getBaseRoleCandidates(selectedRole)}
            isOpen={isEditDialogOpen}
            onClose={() => setIsEditDialogOpen(false)}
            onValidateSuccess={() => {
              setIsEditDialogOpen(false);
              fetchRoles();
            }}
          />
        )}

        {/* PopUp : on delete */}
        {isDeleteDialogOpen && (
          <RoleDeleteDialog
            role={selectedRole}
            isRoleAssigned={isSelectedRoleAssigned}
            baseRoleCandidates={getBaseRoleCandidates(selectedRole)}
            isOpen={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            onValidateSuccess={() => {
              setIsDeleteDialogOpen(false);
              fetchRoles();
            }}
          />
        )}
      </div>
    </div >
  );
}

export default Roles;
